import { type SuggestionKeyDownProps } from '@tiptap/suggestion';
import {
  type RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';

import {
  Popover,
  PopoverAnchor,
  PopoverContent
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface Props {
  anchorRef: RefObject<Measurable | null>;
  command: (item: SlashDropdownValue) => void;
  items: SlashDropdownValue[];
  onOpenChange: (open: boolean) => void;
  open: boolean;
  ref: RefObject<null | SlashCommandListHandle>;
}

export interface SlashCommandListHandle {
  onKeyDown: (props: SuggestionKeyDownProps) => boolean;
}

export interface SlashDropdownValue {
  label: string;
  value: string;
}

interface Measurable {
  getBoundingClientRect: () => DOMRect;
}

const SlashCommandList = ({
  anchorRef,
  command,
  items,
  onOpenChange,
  open,
  ref
}: Props) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedIndex(0);
  }, [items]);

  const handleSelect = useCallback(
    (item: SlashDropdownValue) => {
      command(item);
      onOpenChange(false);
    },
    [command, onOpenChange]
  );

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex(selectedIndex === 0 ? 0 : selectedIndex - 1);
        return true;
      }

      if (event.key === 'ArrowDown') {
        setSelectedIndex(
          selectedIndex === items.length - 1
            ? items.length - 1
            : selectedIndex + 1
        );
        return true;
      }

      if (event.key === 'PageUp') {
        setSelectedIndex(selectedIndex - 10 < 0 ? 0 : selectedIndex - 10);
        return true;
      }

      if (event.key === 'PageDown') {
        setSelectedIndex(
          selectedIndex + 10 > items.length - 1
            ? items.length - 1
            : selectedIndex + 10
        );
        return true;
      }

      if (event.key === 'Home') {
        setSelectedIndex(0);
        return true;
      }

      if (event.key === 'End') {
        setSelectedIndex(items.length - 1);
        return true;
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        const selectedItem = items[selectedIndex];
        handleSelect(selectedItem);
        return true;
      }

      if (event.key === 'Escape') {
        onOpenChange(false);
        return true;
      }

      return false;
    }
  }));

  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    itemRefs.current[selectedIndex]?.scrollIntoView({
      behavior: 'instant',
      block: 'nearest'
    });
  }, [selectedIndex]);

  return (
    <Popover
      modal
      onOpenChange={onOpenChange}
      open={open}
    >
      <PopoverAnchor virtualRef={anchorRef as RefObject<Measurable>} />
      <PopoverContent
        align="start"
        className="max-h-82.5 min-w-64 overflow-y-auto p-1"
        onOpenAutoFocus={e => {
          e.preventDefault();
        }}
        side="bottom"
        sideOffset={8}
      >
        {items.length ? (
          items.map((item, index) => (
            <button
              className={cn(
                'hover:bg-accent flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm',
                index === selectedIndex && 'bg-accent'
              )}
              key={`${item.value}-${item.label}`}
              onClick={() => {
                handleSelect(item);
              }}
              ref={element => {
                itemRefs.current[index] = element;
              }}
              type="button"
            >
              {item.label}
            </button>
          ))
        ) : (
          <div className="w-full rounded-md px-2 py-1.5 text-sm">No result</div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default SlashCommandList;
